import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SiteNavbar from '@/components/feature/SiteNavbar';
import SiteFooter from '@/components/feature/SiteFooter';
import ScrollToTop from '@/components/feature/ScrollToTop';
import RevealWrapper from '@/pages/home/components/RevealWrapper';

const HERO_METRICS = [
  { valueKey: 'websvc.metric.1.value', labelKey: 'websvc.metric.1.label' },
  { valueKey: 'websvc.metric.2.value', labelKey: 'websvc.metric.2.label' },
  { valueKey: 'websvc.metric.3.value', labelKey: 'websvc.metric.3.label' },
] as const;

const VALUE_POINTS = [
  'websvc.value.1',
  'websvc.value.2',
  'websvc.value.3',
  'websvc.value.4',
  'websvc.value.5',
] as const;

const SERVICE_CARDS = [
  { icon: 'ri-compass-3-line', titleKey: 'websvc.card.1.title', bodyKey: 'websvc.card.1.body' },
  { icon: 'ri-brush-line', titleKey: 'websvc.card.2.title', bodyKey: 'websvc.card.2.body' },
  { icon: 'ri-code-s-slash-line', titleKey: 'websvc.card.3.title', bodyKey: 'websvc.card.3.body' },
  { icon: 'ri-rocket-line', titleKey: 'websvc.card.4.title', bodyKey: 'websvc.card.4.body' },
] as const;

const PROCESS_STEPS = [
  { id: '01', titleKey: 'websvc.step.1.title', bodyKey: 'websvc.step.1.body' },
  { id: '02', titleKey: 'websvc.step.2.title', bodyKey: 'websvc.step.2.body' },
  { id: '03', titleKey: 'websvc.step.3.title', bodyKey: 'websvc.step.3.body' },
] as const;

interface WebsiteShowcaseItem {
  id: string;
  name: string;
  logoUrl: string;
  siteUrl?: string;
}

interface WebsiteShowcaseResponse {
  success?: boolean;
  items?: WebsiteShowcaseItem[];
}

const SHOWCASE_COPY = {
  tr: {
    label: 'teslim edilen isler',
    title: "G-Software'in tasarladığı ve teslim ettiği siteler",
    body: 'Hazırladığımız web deneyimlerini burada sergiliyoruz. Her kart, yayına alınmış veya müşteriye teslim edilmiş bir işi temsil eder.',
    emptyTitle: 'Referans alanı hazır',
    emptyBody: 'Bakım kontrol panelinden firma adı ve logo eklediğinde burada otomatik görünecek.',
    loading: 'Referanslar yükleniyor...',
    itemLabel: 'teslim edilen site',
    visitLabel: 'siteye git',
  },
  en: {
    label: 'delivered work',
    title: 'Websites designed and delivered by G-Software',
    body: 'A live showcase of web experiences we have prepared for clients. Each card represents a launched or delivered project.',
    emptyTitle: 'Showcase is ready',
    emptyBody: 'Add a company name and logo from the maintenance control panel and it will appear here automatically.',
    loading: 'Loading showcase...',
    itemLabel: 'delivered website',
    visitLabel: 'visit site',
  },
} as const;

function WebsiteShowcaseSection() {
  const { i18n } = useTranslation();
  const [items, setItems] = useState<WebsiteShowcaseItem[]>([]);
  const [loading, setLoading] = useState(true);
  const copy = i18n.resolvedLanguage?.startsWith('en') ? SHOWCASE_COPY.en : SHOWCASE_COPY.tr;

  useEffect(() => {
    let mounted = true;

    fetch('/api/showcase', { headers: { Accept: 'application/json' } })
      .then(async (response) => {
        if (!response.ok) return;
        const data = (await response.json().catch(() => ({}))) as WebsiteShowcaseResponse;
        if (mounted) setItems(Array.isArray(data.items) ? data.items : []);
      })
      .catch(() => {
        if (mounted) setItems([]);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <section className="border-b border-white/6 bg-[#0c0c0c]">
      <div className="px-6 lg:px-14 py-8 border-b border-white/6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <span className="font-mono text-xs text-neutral-600 tracking-widest uppercase">{copy.label}</span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight leading-tight max-w-4xl">
            {copy.title}
          </h2>
        </div>
        <p className="text-neutral-500 text-sm leading-relaxed max-w-md md:text-right">{copy.body}</p>
      </div>

      <div className="px-6 lg:px-14 py-10">
        {loading ? (
          <div className="border border-white/8 bg-[#101010] px-6 py-8">
            <p className="font-mono text-xs text-neutral-500 tracking-widest uppercase">{copy.loading}</p>
          </div>
        ) : items.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 border-t border-l border-white/10">
            {items.map((item, index) => {
              const card = (
                <article className="group min-h-[210px] h-full bg-[#101010] p-6 flex flex-col justify-between hover:bg-[#151515] transition-colors duration-300">
                  <div className="flex items-start justify-between gap-4">
                    <div className="w-16 h-16 border border-white/12 bg-black/30 flex items-center justify-center overflow-hidden">
                      <img
                        src={item.logoUrl}
                        alt={`${item.name} logo`}
                        className="w-12 h-12 object-contain opacity-85 group-hover:opacity-100 transition-opacity duration-300"
                        loading="lazy"
                      />
                    </div>
                    {item.siteUrl && (
                      <i className="ri-arrow-right-up-line text-white/35 group-hover:text-white transition-colors duration-300" />
                    )}
                  </div>
                  <div className="pt-10">
                    <p className="font-mono text-[10px] text-neutral-600 tracking-widest uppercase mb-2">{copy.itemLabel}</p>
                    <h3 className="text-xl font-black text-white tracking-tight">{item.name}</h3>
                    {item.siteUrl && (
                      <p className="mt-4 font-mono text-[10px] text-white/45 tracking-widest uppercase group-hover:text-white/80 transition-colors duration-300">
                        {copy.visitLabel}
                      </p>
                    )}
                  </div>
                </article>
              );

              return (
                <RevealWrapper
                  key={item.id}
                  direction="up"
                  delay={index * 60}
                  threshold={0.1}
                  className="border-r border-b border-white/10"
                >
                  {item.siteUrl ? (
                    <a
                      href={item.siteUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="block h-full cursor-pointer"
                    >
                      {card}
                    </a>
                  ) : (
                    card
                  )}
                </RevealWrapper>
              );
            })}
          </div>
        ) : (
          <div className="border border-white/8 bg-[#101010] p-7 lg:p-9">
            <p className="font-mono text-[10px] text-neutral-600 tracking-widest uppercase mb-3">{copy.emptyTitle}</p>
            <p className="text-neutral-400 text-sm leading-relaxed max-w-xl">{copy.emptyBody}</p>
          </div>
        )}
      </div>
    </section>
  );
}

export default function WebsiteOfferPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const OFFER_LINKS = [
    { label: t('nav.home'), href: '/', isRoute: true },
    { label: t('nav.apps'), href: '/apps', isRoute: true },
    { label: t('nav.games'), href: '/games', isRoute: true },
    { label: t('nav.contact'), href: '#contact' },
  ];

  return (
    <div className="min-h-screen bg-[#0c0c0c]">
      <SiteNavbar links={OFFER_LINKS} sectionAware={false} />

      <section className="relative overflow-hidden pt-32 pb-16 border-b border-white/6">
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.14]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)',
            backgroundSize: '74px 74px',
          }}
        />
        <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_15%_18%,rgba(255,255,255,0.08),transparent_40%),radial-gradient(circle_at_80%_20%,rgba(255,255,255,0.05),transparent_40%)]" />

        <div className="relative px-6 lg:px-14">
          <div className="grid xl:grid-cols-12 gap-8 xl:gap-12 items-start">
            <RevealWrapper direction="up" threshold={0.1} className="xl:col-span-8">
              <p className="font-mono text-xs text-white/60 tracking-widest uppercase mb-4">{t('websvc.label')}</p>
              <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black text-white tracking-tight leading-none max-w-5xl">
                {t('websvc.title.line1')} <span className="text-neutral-300">{t('websvc.title.accent')}</span>
              </h1>
              <p className="mt-6 text-neutral-300 text-base lg:text-lg leading-relaxed max-w-3xl">{t('websvc.subtitle')}</p>
              <p className="mt-4 text-neutral-500 text-sm lg:text-base leading-relaxed max-w-3xl">{t('websvc.lead')}</p>
            </RevealWrapper>

            <RevealWrapper direction="up" delay={120} threshold={0.1} className="xl:col-span-4">
              <div className="border border-white/10 bg-[#101010] p-6">
                <p className="font-mono text-[10px] text-neutral-600 tracking-widest uppercase mb-4">{t('websvc.metrics.label')}</p>
                <div className="space-y-3">
                  {HERO_METRICS.map((metric) => (
                    <div key={metric.valueKey} className="flex items-center justify-between border border-white/8 px-4 py-3">
                      <span className="font-mono text-[10px] text-neutral-500 tracking-widest uppercase">{t(metric.labelKey)}</span>
                      <span className="font-mono text-xs text-white tracking-widest uppercase">{t(metric.valueKey)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </RevealWrapper>
          </div>
        </div>
      </section>

      <section className="border-b border-white/6 bg-[#111111]">
        <div className="px-6 lg:px-14 py-8 border-b border-white/6 flex items-center justify-between gap-4">
          <span className="font-mono text-xs text-neutral-600 tracking-widest uppercase">{t('websvc.value.label')}</span>
          <span className="font-mono text-xs text-neutral-700">{VALUE_POINTS.length} {t('common.items')}</span>
        </div>

        <div className="px-6 lg:px-14 py-10 grid lg:grid-cols-12 gap-6">
          <RevealWrapper direction="up" threshold={0.1} className="lg:col-span-7">
            <div className="border border-white/8 p-6 lg:p-8 h-full">
              <p className="font-mono text-[10px] text-neutral-600 tracking-widest uppercase mb-5">{t('websvc.value.title')}</p>
              <div className="space-y-0">
                {VALUE_POINTS.map((key, idx) => (
                  <div key={key} className={`flex items-start gap-3 py-4 ${idx < VALUE_POINTS.length - 1 ? 'border-b border-white/8' : ''}`}>
                    <i className="ri-arrow-right-up-line text-white/70 mt-0.5" />
                    <p className="text-neutral-300 text-sm leading-relaxed tracking-[0.01em]">{t(key)}</p>
                  </div>
                ))}
              </div>
            </div>
          </RevealWrapper>

          <RevealWrapper direction="up" delay={120} threshold={0.1} className="lg:col-span-5">
            <div className="border border-white/8 p-6 lg:p-8 h-full">
              <p className="font-mono text-[10px] text-neutral-600 tracking-widest uppercase mb-4">{t('websvc.pitch.label')}</p>
              <h3 className="text-2xl lg:text-3xl font-black text-white leading-tight tracking-tight">{t('websvc.pitch.title')}</h3>
              <p className="mt-4 text-neutral-400 text-sm leading-relaxed">{t('websvc.pitch.body')}</p>
              <div className="mt-6 pt-6 border-t border-white/10">
                <p className="font-mono text-xs text-white/85 tracking-widest uppercase">{t('websvc.pitch.result')}</p>
              </div>
            </div>
          </RevealWrapper>
        </div>
      </section>

      <section className="border-b border-white/6 bg-[#0c0c0c]">
        <div className="px-6 lg:px-14 py-8 border-b border-white/6">
          <span className="font-mono text-xs text-neutral-600 tracking-widest uppercase">{t('websvc.services.label')}</span>
        </div>

        <div className="grid md:grid-cols-2 xl:grid-cols-4 border-t border-l border-white/10">
          {SERVICE_CARDS.map((card, idx) => (
            <RevealWrapper key={card.titleKey} direction="up" delay={idx * 70} threshold={0.1} className="h-full border-r border-b border-white/10">
              <div className="h-full bg-[#101010] p-7 lg:p-8 group hover:bg-[#141414] transition-colors duration-300">
                <i className={`${card.icon} text-base text-white/65 group-hover:text-white mb-5 transition-colors duration-300 inline-block`} />
                <h3 className="text-lg font-black text-white mb-3 tracking-tight">{t(card.titleKey)}</h3>
                <p className="text-neutral-500 text-sm leading-relaxed">{t(card.bodyKey)}</p>
              </div>
            </RevealWrapper>
          ))}
        </div>
      </section>

      <section className="border-b border-white/6 bg-[#111111]">
        <div className="px-6 lg:px-14 py-8 border-b border-white/6">
          <span className="font-mono text-xs text-neutral-600 tracking-widest uppercase">{t('websvc.process.label')}</span>
        </div>
        <div className="px-6 lg:px-14 py-10 grid md:grid-cols-3 gap-6">
          {PROCESS_STEPS.map((step) => (
            <RevealWrapper key={step.id} direction="up" threshold={0.1}>
              <div className="border border-white/8 p-6 h-full hover:bg-white/[0.02] transition-colors duration-300">
                <div className="font-mono text-3xl font-black text-white/20 mb-4">{step.id}</div>
                <h4 className="text-lg font-black text-white mb-2">{t(step.titleKey)}</h4>
                <p className="text-neutral-500 text-sm leading-relaxed">{t(step.bodyKey)}</p>
              </div>
            </RevealWrapper>
          ))}
        </div>
      </section>

      <WebsiteShowcaseSection />

      <section className="px-6 lg:px-14 py-16 lg:py-24">
        <RevealWrapper direction="up" threshold={0.1}>
          <div className="max-w-5xl border border-white/10 bg-[#101010] p-8 lg:p-12">
            <h2 className="text-3xl sm:text-4xl lg:text-6xl font-black text-white tracking-tight leading-tight">{t('websvc.finalCta')}</h2>
            <p className="mt-5 text-neutral-500 text-base leading-relaxed max-w-3xl">{t('websvc.finalSub')}</p>
            <button
              onClick={() => navigate('/?contactProduct=general&contactSubject=website-request#contact')}
              className="mt-10 inline-flex items-center gap-2 px-7 py-4 border border-white/35 text-white font-mono text-xs tracking-widest uppercase hover:bg-white hover:text-black transition-all duration-300 cursor-pointer"
            >
              <i className="ri-mail-send-line" />
              {t('websvc.cta')}
            </button>
          </div>
        </RevealWrapper>
      </section>

      <SiteFooter />
      <ScrollToTop />
    </div>
  );
}
